import { useEffect, useCallback } from "react";

/**
 * Enhanced mobile viewport hook
 * Handles dynamic viewport height for mobile browsers (Chrome/Safari)
 * Provides fallback for browsers that don't support dvh and enhanced detection
 */
function useMobileViewport() {
  const setViewportProperties = useCallback(() => {
    // Get the actual viewport dimensions
    const vh = window.innerHeight * 0.01;
    const vw = window.innerWidth * 0.01;

    // Set CSS custom properties
    const root = document.documentElement;
    root.style.setProperty("--vh", `${vh}px`);
    root.style.setProperty("--vw", `${vw}px`);

    // Calculate available height (excluding browser UI)
    const availableHeight = window.innerHeight;
    root.style.setProperty("--available-height", `${availableHeight}px`);

    // Detect if we're in a mobile browser with dynamic UI
    const isMobile =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari =
      /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent);

    // Set browser-specific properties
    root.style.setProperty("--is-mobile", isMobile ? "1" : "0");
    root.style.setProperty("--is-ios", isIOS ? "1" : "0");
    root.style.setProperty("--is-safari", isSafari ? "1" : "0");
    root.style.setProperty("--is-chrome", isChrome ? "1" : "0");
  }, []);

  useEffect(() => {
    // Check if browser supports dvh
    const supportsDVH = CSS.supports("height", "100dvh");
    const supportsLVH = CSS.supports("height", "100lvh");
    const supportsSVH = CSS.supports("height", "100svh");

    // Set support flags
    const root = document.documentElement;
    root.style.setProperty("--supports-dvh", supportsDVH ? "1" : "0");
    root.style.setProperty("--supports-lvh", supportsLVH ? "1" : "0");
    root.style.setProperty("--supports-svh", supportsSVH ? "1" : "0");

    // Always run the viewport calculation for consistency
    // Even modern browsers benefit from the additional properties we set
    setViewportProperties();

    // Debounced resize handler
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        requestAnimationFrame(setViewportProperties);
      }, 100);
    };

    // Orientation change handler with delay for browser UI settling
    const handleOrientationChange = () => {
      // Multiple timeouts to catch different phases of orientation change
      setTimeout(setViewportProperties, 100);
      setTimeout(setViewportProperties, 300);
      setTimeout(setViewportProperties, 500);
    };

    // Visual viewport API support (for better keyboard handling)
    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        const visualHeight = window.visualViewport.height;
        root.style.setProperty("--visual-viewport-height", `${visualHeight}px`);

        // Detect if keyboard is likely open (significant height difference)
        const heightDifference = window.innerHeight - visualHeight;
        const keyboardOpen = heightDifference > 150; // Threshold for keyboard detection
        root.style.setProperty("--keyboard-open", keyboardOpen ? "1" : "0");
      }
    };

    // Event listeners
    window.addEventListener("resize", handleResize, { passive: true });
    window.addEventListener("orientationchange", handleOrientationChange);

    // Visual viewport API for keyboard detection
    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        handleVisualViewportChange,
        { passive: true }
      );
    }

    // Focus/blur events for input handling
    const handleFocusIn = (e: FocusEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        root.style.setProperty("--input-focused", "1");
        // Delay to allow keyboard to appear
        setTimeout(setViewportProperties, 300);
      }
    };

    const handleFocusOut = () => {
      root.style.setProperty("--input-focused", "0");
      // Delay to allow keyboard to disappear
      setTimeout(setViewportProperties, 300);
    };

    document.addEventListener("focusin", handleFocusIn, { passive: true });
    document.addEventListener("focusout", handleFocusOut, { passive: true });

    // Cleanup
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleVisualViewportChange
        );
      }

      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [setViewportProperties]);
}

export { useMobileViewport };
